import { FormControlLabel, Grid, Switch } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useGetMeasureUnitsQuery, useGetTemperatureRangesQuery } from '../../../app/redux-fetch/apiCoreQuery';
import { convertToLabelValue } from '../../../app/utils';
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid';
import CombinedSelect from '../../../components/Forms/CombinedSelect';
import FormInput from '../../../components/Forms/FormInput';
import FormSelect from '../../../components/Forms/FormSelect';
import messages from './messages';

interface TemperatureControlledFormProps {
    consignmentIndex: number;
    packagingIndex: number;
    disabled?: boolean;
}

const TemperatureControlledForm: React.FC<TemperatureControlledFormProps> = ({ consignmentIndex, packagingIndex, disabled = false }) => {
    const { control, watch, setValue } = useFormContext();
    const isTemperatureControlled = watch(`bookings.${consignmentIndex}.packaging.${packagingIndex}.temperatureControlled`);

    const unit = watch(`bookings.${consignmentIndex}.packaging.${packagingIndex}.setPointUnitId`);

    const { data: measureUnits = [] } = useGetMeasureUnitsQuery()
    const { data: temperatureRange = [] } = useGetTemperatureRangesQuery()

    const temperatureUnitsOptions = useMemo(() => {
        return convertToLabelValue(measureUnits.filter(x => x.measureTypeId === 3).map(x => {
            return { name: x.units, id: x.id }
        }))
    }, [measureUnits])


    const temperatureRangeOptions = useMemo(() => {
        return convertToLabelValue(temperatureRange)
    }, [temperatureRange])

    const getTemperatureUnit = useCallback((id: number) => {
        return measureUnits.find((unit) => unit.id === id)?.units;
    }, [measureUnits])

    const { formatMessage } = useIntl();
    const resetTemperatureControlled = useCallback(() => {
        const fieldsToReset = [
            'temperatureRange',
            'setPoint',
            'setPointUnitId',
            'lowerWarning',
            'upperWarning',
            'lowerCritical',
            'upperCritical'
        ];
        fieldsToReset.forEach(field => setValue(`bookings.${consignmentIndex}.packaging.${packagingIndex}.${field}`, undefined));
    }, [consignmentIndex, packagingIndex, setValue]);

    return (
        <DefaultSpacingGrid item xs={12} container sx={{ pb: isTemperatureControlled ? 0 : '16px', mt: 0 }}>
            <Grid item xs={12}>
                <Controller
                    name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.temperatureControlled`}
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                        <FormControlLabel
                            control={<Switch {...field} checked={field.value}
                                onChange={(e) => {
                                    field.onChange(e.target.checked)
                                    resetTemperatureControlled()
                                }}
                                disabled={disabled} />}
                            label={formatMessage(messages.temperatureControlled)}
                        />
                    )}
                />
            </Grid>
            {isTemperatureControlled && (
                <>
                    <Grid item xs={12} md={2}>
                        <FormSelect
                            name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.temperatureRange`}
                            label={formatMessage(messages.temperatureRange)}
                            options={temperatureRangeOptions}
                            fullWidth
                            required={true}
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <CombinedSelect
                            nameValue={`bookings.${consignmentIndex}.packaging.${packagingIndex}.setPoint`}
                            nameUnit={`bookings.${consignmentIndex}.packaging.${packagingIndex}.setPointUnitId`}
                            labelValue={formatMessage(messages.temperatureSetPoint)}
                            labelUnitValue={formatMessage(messages.unitLabel)}
                            optionsUnit={temperatureUnitsOptions}
                            showLabel={false}
                            required={true}
                            numberFormatProps={{
                                decimalScale: 4,
                                fixedDecimalScale: true,
                                allowNegative: true,
                            }}
                            disabled={disabled}
                            maxValue={9999.9999}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormInput
                            name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.lowerCritical`}
                            label={formatMessage(messages.lowerCritical)}
                            InputLabelProps={{ shrink: true }}
                            type='number'
                            unit={getTemperatureUnit(unit)}
                            fullWidth
                            required={true}
                            numberFormatProps={{
                                decimalScale: 4,
                                fixedDecimalScale: true,
                                allowNegative: true,
                            }}
                            disabled={disabled}
                            maxValue={9999.9999}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormInput
                            name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.lowerWarning`}
                            label={formatMessage(messages.lowerWarning)}
                            InputLabelProps={{ shrink: true }}
                            type='number'
                            unit={getTemperatureUnit(unit)}
                            fullWidth
                            numberFormatProps={{
                                decimalScale: 4,
                                fixedDecimalScale: true,
                                allowNegative: true,
                            }}
                            disabled={disabled}
                            maxValue={9999.9999}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormInput
                            name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.upperWarning`}
                            label={formatMessage(messages.upperWarning)}
                            InputLabelProps={{ shrink: true }}
                            type='number'
                            unit={getTemperatureUnit(unit)}
                            numberFormatProps={{
                                decimalScale: 4,
                                fixedDecimalScale: true,
                                allowNegative: true,
                            }}
                            fullWidth
                            disabled={disabled}
                            maxValue={9999.9999}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormInput
                            name={`bookings.${consignmentIndex}.packaging.${packagingIndex}.upperCritical`}
                            label={formatMessage(messages.upperCritical)}
                            InputLabelProps={{ shrink: true }}
                            type='number'
                            unit={getTemperatureUnit(unit)}
                            fullWidth
                            required={true}
                            numberFormatProps={{
                                decimalScale: 4,
                                fixedDecimalScale: true,
                                allowNegative: true,
                            }}
                            disabled={disabled}
                            maxValue={9999.9999}
                        />
                    </Grid>
                </>
            )}
        </DefaultSpacingGrid>
    );
};

export default TemperatureControlledForm;
